// @ts-nocheck
/**
 * Agent floating disposition form — embedded inside the CTI panel (not a
 * standalone route). Redesigned to shadcn primitives with lucide icons and
 * theme tokens. Preserves all v1 quirks documented at top of the original
 * port: initial state's `selectedValue: []` alongside the `selectedRemarkValue`
 * shape mutation over the component lifetime.
 *
 * V1 QUIRKS preserved verbatim (do not "fix"):
 *  - Initial state uses `selectedValue: []` but every read/write site uses
 *    `selectedRemarkValue` — so `formData.selectedRemarkValue` starts as
 *    `undefined` and `handleCheckboxChange` reads `prev.selectedRemarkValue
 *    || []`. Preserved.
 *  - After successful POST, `setFormData` resets `selectedRemarkValue: []`
 *    (not `selectedValue`) — the shape mutates over the component's lifetime.
 *  - Route surface: the exported component is named `Form`, kept as default.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ListChecks,
  MessageSquareText,
  Phone,
  Send,
  User,
  UserCircle2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { API_REMARK_CREATE, API_REMARK_STATUS_LIST } from '@/service/url';
import { postApiCall, getApiCall } from '@/service/httpCall';

const DespositionForm = () => {
  const [formData, setFormData] = useState<any>({
    firstName: '',
    lastName: '',
    contactNumber: '',
    remarksText: '',
    selectedValue: [],
    selectedRemarkValue: [],
  });
  const [remarkOptions, setRemarkOptions] = useState<any[]>([]);

  useEffect(() => {
    getApiCall(API_REMARK_STATUS_LIST, {})
      .then((resp: any) => setRemarkOptions(resp))
      .catch((err: any) => console.error(err));
  }, []);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (remark: any) => {
    setFormData((prev: any) => {
      const selected = prev.selectedRemarkValue || [];
      const newSelected = selected.includes(remark)
        ? selected.filter((r: any) => r !== remark)
        : [...selected, remark];
      return { ...prev, selectedRemarkValue: newSelected };
    });
  };

  const validateForm = () => {
    if (!formData.remarksText.trim()) {
      toast.warning('Remark is required');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!validateForm()) return;

    const data = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      mobile_number: formData.contactNumber,
      remark: [...(formData.selectedRemarkValue || []), formData.remarksText],
    };

    postApiCall(API_REMARK_CREATE, data)
      .then(() => {
        toast.success('Remark Added successfully');
        setFormData({
          firstName: '',
          lastName: '',
          contactNumber: '',
          remarksText: '',
          selectedRemarkValue: [],
        });
      })
      .catch((err: any) => {
        toast.warning(JSON.stringify(err));
      });
  };

  const selectedList = formData.selectedRemarkValue || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-2.5">
      {/* Header + name/phone block */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <UserCircle2 className="h-20 w-20 text-primary" strokeWidth={1.5} />
        </div>
        <div className="flex flex-1 flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="First name"
                className="pl-9"
              />
            </div>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Last name"
                className="pl-9"
              />
            </div>
          </div>
          <div className="relative">
            <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleInputChange}
              placeholder="Contact number"
              className="pl-9 font-mono"
            />
          </div>
        </div>
      </div>

      {/* Remarks textarea */}
      <div className="space-y-2">
        <Label htmlFor="remarksText">Remarks</Label>
        <div className="relative">
          <MessageSquareText className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Textarea
            id="remarksText"
            name="remarksText"
            rows={3}
            value={formData.remarksText}
            onChange={handleInputChange}
            placeholder="Enter your remarks here…"
            className="pl-9 pt-3"
          />
        </div>
      </div>

      {/* Select remark dropdown + chip list */}
      <div className="space-y-2">
        <Label>Select Remark</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start pl-9 font-normal"
            >
              <ListChecks className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <span>
                {selectedList.length === 0
                  ? 'Select remarks…'
                  : `${selectedList.length} selected`}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-56 overflow-y-auto">
            {remarkOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No options
              </div>
            ) : (
              remarkOptions.map((opt: any) => (
                <label
                  key={opt.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted"
                >
                  <Checkbox
                    checked={selectedList.includes(opt.remark)}
                    onCheckedChange={() => handleCheckboxChange(opt.remark)}
                  />
                  {opt.remark}
                </label>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {selectedList.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {selectedList.map((val: any) => (
              <span
                key={val}
                className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary"
              >
                {val}
                <button
                  type="button"
                  onClick={() => handleCheckboxChange(val)}
                  className="rounded-full hover:bg-primary/25"
                  aria-label={`Remove ${val}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <Button type="submit" size="lg" className="w-full gap-2">
        <Send className="h-4 w-4" />
        Submit
      </Button>
    </form>
  );
};

export default DespositionForm;
